<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

// MOBILE MENU
const isOpen = ref(false)
const toggleMenu = () => (isOpen.value = !isOpen.value)
const closeMenu = () => (isOpen.value = false)

// DROPDOWN MENU
const isDropdownOpen = ref(false)
const toggleDropdown = () => (isDropdownOpen.value = !isDropdownOpen.value)
const closeDropdown = () => (isDropdownOpen.value = false)

// CLOSE DROPDOWN WHEN CLICKING OUTSIDE
const handleClickOutside = (e) => {
  if (!e.target.closest('.dropdown') && !e.target.closest('.mobile-dropdown')) {
    isDropdownOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// CLOSE DROPDOWN WHEN ROUTE CHANGES
const route = useRoute()
watch(() => route.fullPath, () => {
  closeDropdown()
})

// HIDE NAV ON SCROLL
const showNav = ref(true)
let lastScrollY = window.scrollY
const handleScroll = () => {
  const currentY = window.scrollY
  showNav.value = currentY < lastScrollY || currentY <= 80
  lastScrollY = currentY
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <section class="fullnav" :class="{ hiddennav: !showNav }">
    <!-- LOGO -->
  <div class="nav-center">
  <RouterLink to="/" class="logo-link" @click="closeMenu">
    <img src="../assets/img/knifelogo.svg" alt="Logo" class="nav-logo" />
  </RouterLink>
</div>


    <!-- NAVBAR -->
    <nav class="navbar">
      <!-- HAMBURGER MOBILE -->
      <button class="hamburger" @click="toggleMenu">
        <span :class="{ open: isOpen }"></span>
        <span :class="{ open: isOpen }"></span>
        <span :class="{ open: isOpen }"></span>
      </button>

      <section class="nav-grid">
        <!-- DESKTOP MENU -->
        <section class="nav-menu desktop">
          <ul>
            <li><RouterLink to="/">{{ t('nav.home') }}</RouterLink></li>

            <!-- DROPDOWN -->
            <li class="dropdown">
              <button class="dropbtn" @click.stop="toggleDropdown">
                {{ t('nav.brands') }} <i class="fa fa-caret-down"></i>
              </button>
              <transition name="slide-fade">
                <ul v-if="isDropdownOpen" class="dropdown-content">
                  <li><RouterLink to="/hungrys" @click="closeDropdown">{{ t('brands.hungrys') }}</RouterLink></li>
                  <li><RouterLink to="/whealthy" @click="closeDropdown">{{ t('brands.whealthy') }}</RouterLink></li>
                  <!-- <li><RouterLink to="/samosahouse" @click="closeDropdown">{{ t('brands.samosahouse') }}</RouterLink></li>
                  <li><RouterLink to="/chikoschicken" @click="closeDropdown">{{ t('brands.chikoschicken') }}</RouterLink></li> -->
                </ul>
              </transition>
            </li>

            <li><RouterLink to="/catering">{{ t('nav.catering') }}</RouterLink></li>
            <li><RouterLink to="/venue">{{ t('nav.venue') }}</RouterLink></li>
            <li><RouterLink to="/aboutus">{{ t('nav.about') }}</RouterLink></li>
            <li><RouterLink to="/contact">{{ t('nav.contact') }}</RouterLink></li>
          </ul>
        </section>

        <!-- LANGUAGE -->
        <section class="nav-right desktop">
          <button @click="locale='en'" class="lang">EN</button>
          <button @click="locale='fr'" class="lang">FR</button>
        </section>
      </section>
    </nav>

    <!-- MOBILE MENU -->
    <transition name="fade">
      <section v-if="isOpen" class="mobile-menu">
        <ul>
          <li><RouterLink to="/" @click="closeMenu">{{ t('nav.home') }}</RouterLink></li>

          <!-- MOBILE DROPDOWN -->
          <li class="mobile-dropdown">
            <button class="mobile-dropbtn" @click.stop="toggleDropdown">
              {{ t('nav.brands') }} <i class="fa fa-caret-down"></i>
            </button>
            <transition name="slide-fade">
              <ul v-if="isDropdownOpen" class="mobile-dropdown-content">
                <li><RouterLink to="/hungrys" @click="() => { closeDropdown(); closeMenu(); }">{{ t('brands.hungrys') }}</RouterLink></li>
                <li><RouterLink to="/whealthy" @click="() => { closeDropdown(); closeMenu(); }">{{ t('brands.whealthy') }}</RouterLink></li>
                <!-- <li><RouterLink to="/samosahouse" @click="() => { closeDropdown(); closeMenu(); }">{{ t('brands.samosahouse') }}</RouterLink></li>
                <li><RouterLink to="/chikoschicken" @click="() => { closeDropdown(); closeMenu(); }">{{ t('brands.chikoschicken') }}</RouterLink></li> -->
              </ul>
            </transition>
          </li>

          <li><RouterLink to="/catering" @click="closeMenu">{{ t('nav.catering') }}</RouterLink></li>
          <li><RouterLink to="/venue" @click="closeMenu">{{ t('nav.venue') }}</RouterLink></li>
          <li><RouterLink to="/aboutus" @click="closeMenu">{{ t('nav.about') }}</RouterLink></li>
          <li><RouterLink to="/contact" @click="closeMenu">{{ t('nav.contact') }}</RouterLink></li>
        </ul>

        <button @click="locale='en'" class="lang-mobile">EN</button>
        <button @click="locale='fr'" class="lang-mobile">FR</button>
      </section>
    </transition>
  </section>
</template>


<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css');
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap');

/* NAVBAR STYLES */
.fullnav {
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
  transition: transform 0.35s ease;
}
.fullnav.hiddennav { transform: translateY(-100%); }

.nav-center {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.6rem 0;
  background: white;
}

.nav-logo {
  height: 60px;
  width: auto;
  max-width: 90vw;
  object-fit: contain;
  display: block;
}

.navbar {
   background: #39383d; 
   color: white; 
   height: 50px; 
   display: grid; 
   place-items: center; 
   position: relative; 
   padding: 0 2rem; 
  }
.nav-grid {
   display: grid; 
   grid-template-columns: 1fr auto;
    align-items: center; 
    width: 100%; 
    max-width: 1300px; 
  }

.nav-menu ul {
   display: flex; 
   gap: 2rem; 
   list-style: none; 
   align-items: center; 
   
   justify-content: center; 
  }
.nav-menu a {
   color: white; 
   text-decoration: none; 
   font-size:clamp(0.9rem, 1vw, 1rem); 
   font-weight: 500; 
   font-family: 'Montserrat', sans-serif; 
   padding: 8px 0; 
   transition: opacity 0.2s; 
  }
.nav-menu a:hover { 
  opacity: 0.7; 
}

.dropdown {
   position: relative; 
  }
.dropbtn {
   font-size:clamp(0.9rem, 1vw, 0.8rem); 
   color: white; 
   background: transparent; 
   border: none; 
   cursor: pointer; 
   display: flex; 
   align-items: center; 
   font-weight: 500; 
   padding: 8px 0; 
   font-family: 'Montserrat', sans-serif;
  
  }
.dropdown-content {
   position: absolute; 
   top: 120%; 
   right: -15px; 
   min-width: 160px; 
   padding: 8px 0; 
   background: #4b4a4f; 
   box-shadow: 0 8px 20px rgba(0,0,0,0.18); 
   max-height: none; 
   overflow: visible; 
   display: flex; 
   flex-direction: column; 
   font-size: 0.9rem;
  }
.dropdown-content a {
   
   border-radius: 0; 
  }
.dropdown-content a:hover {

   transition: all 0.2s ease; 
  }

i {
   margin-left: 6px; 
   font-size: 0.8rem; 
  }
.nav-right {
   justify-self: end; 
  }
.lang {
   color: white; 
   text-decoration: none; 
   font-size: 0.9rem; 
   font-family:  'Montserrat', sans-serif;
    background: none;
    border: 1px solid white;
    padding: 4px 10px;
   
  }
.lang:hover {
   background: white; 
   color: #39383d;
}




.hamburger {
   display: none; 
   flex-direction: column; 
   justify-content: space-between; 
   width: 35px; 
   height: 20px; 
   background: none; 
   border: none; 
   cursor: pointer; 
   position: absolute; 
   right: 1.5rem; 
   top: 50%; 
   transform: translateY(-50%); 
   z-index: 1100; 
  }
.hamburger span {
   display: block; 
   height: 2px; 
   width: 100%; 
   background: white; 
   border-radius: 2px; 
   transition: all 0.3s ease; 
  }
.hamburger span.open:nth-child(1) {
   transform: rotate(-45deg) translateY(13px); 
  }
.hamburger span.open:nth-child(2) {
   opacity: 0; 
  }
.hamburger span.open:nth-child(3) {
   transform: rotate(45deg) translateY(-13px); 
  }

.mobile-menu {
   position: fixed; 
   inset: 0; 
   background: #39383d; 
   z-index: 1000; 
   display: flex; 
   flex-direction: column; 
   align-items: center; 
   justify-content: center; 
  }
.mobile-menu ul {
   list-style: none; 
   text-align: center; 
   padding: 0; 
  }
.mobile-menu li {
   margin: 1.5rem 0; 
  }
.mobile-menu a {
   color: white; 
   font-size: 1.4rem; 
   text-decoration: none; 
   letter-spacing: 1px; 
   font-family: 'Montserrat', sans-serif; 
   transition: opacity 0.2s; 
  }
.mobile-menu a:hover {
   opacity: 0.7; 
  }
.lang-mobile {
   margin-top: 1rem; 
   color: white; 
   text-decoration: none; 
   font-size: 1rem; 
   border: 1px solid white; 
   padding: 0.5rem 1rem; 
   text-decoration: none; 
   font-family:  'Montserrat', sans-serif;
    background: none;


  }

.mobile-dropdown {
   text-align: center; 
  }
.mobile-dropbtn { 
  background: none; 
  border: none; 
  color: white; 
  font-size: 1.4rem; 
  cursor: pointer; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  font-family:  'Montserrat', sans-serif;
}
.mobile-dropbtn i {
   margin-left: 8px; 
  }
.mobile-dropdown-content {
   margin-top: 0.5rem; 
   display: flex; 
   flex-direction: column; 
   gap: 0.8rem; 
   text-decoration: none; 
   color: white; 
  }
.mobile-dropdown-content a {
   font-size: 1.2rem; 
  }

.fade-enter-active, .fade-leave-active {
   transition: opacity 0.3s ease; 
  }
.fade-enter-from, .fade-leave-to {
   opacity: 0; 
  }

.slide-fade-enter-active, .slide-fade-leave-active {
   transition: all 0.22s cubic-bezier(0.25,0.1,0.25,1); 
   transform-origin: top; 
  }
.slide-fade-enter-from {
   opacity:0; 
   transform: translateY(-6px) scale(0.98); 
  }
.slide-fade-enter-to {
   opacity:1; 
   transform: translateY(0) scale(1); 
  }
.slide-fade-leave-from {
   opacity:1; 
   transform: translateY(0) scale(1); 
  }
.slide-fade-leave-to {
   opacity:0; 
   transform: translateY(-6px) scale(0.98); 
  }

@media (max-width:    920px) {
  .hamburger { display: flex; }
  .desktop { display: none; }
  
}
</style>